import React, { useEffect, useState, useRef } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Card from '../ui/Card.tsx';

interface CollectionValueCardProps {
  value: number;
  previousValue?: number;
  currency?: string;
  loading?: boolean;
}

export default function CollectionValueCard({
  value,
  previousValue,
  currency = 'EUR',
  loading = false
}: CollectionValueCardProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const animationRef = useRef<number>();

  // Animated counter effect
  useEffect(() => {
    if (loading) return;

    const startValue = displayValue;
    const endValue = value;
    const duration = 1500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (endValue - startValue) * eased;

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, loading]);

  const change = previousValue ? value - previousValue : 0;
  const changePercent = previousValue ? ((change / previousValue) * 100) : 0;
  const isPositive = change >= 0;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  if (loading) {
    return (
      <Card variant="elevated" padding="lg">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-24 mb-3" />
          <div className="h-10 bg-gray-700 rounded w-48 mb-2" />
          <div className="h-4 bg-gray-700 rounded w-32" />
        </div>
      </Card>
    );
  }

  return (
    <Card variant="elevated" padding="lg" className="relative overflow-hidden">
      {/* Background glow effect */}
      <div
        className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-20 ${
          isPositive ? 'bg-green-500' : 'bg-red-500'
        }`}
      />

      <div className="relative">
        <p className="text-gray-400 text-sm font-medium mb-1">Collection Value</p>

        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-white tabular-nums">
            {formatCurrency(displayValue)}
          </span>
        </div>

        {previousValue !== undefined && (
          <div className="flex items-center gap-2 mt-2">
            <div
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium ${
                isPositive
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}
            >
              {isPositive ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              <span>
                {isPositive ? '+' : ''}{changePercent.toFixed(1)}%
              </span>
            </div>
            <span className="text-gray-500 text-sm">
              {isPositive ? '+' : ''}{formatCurrency(change)} this month
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
